import { useMemo, useState } from 'react';
import styled from 'styled-components';
import {
  apiErrorText,
  useConfirmPtoVolumeEditMutation,
  useDeclinePtoVolumeEditMutation,
  useListPtoVolumeEditsQuery,
  useResolvePtoVolumeEditMutation,
  type PtoVolumeEdit,
  type PtoVolumePosition,
} from '@shared/api';
import { Button } from '@shared/ui/Button';
import { Select } from '@shared/ui/Input';
import { rowLabel } from '../volumes/rowRef';
import {
  ATTENTION_STATUSES,
  canConfirmPending,
  dateTimeRu,
  editValuesOf,
  isManualRow,
  isTableRow,
  kindLabel,
  sheetRefText,
  statusLabel,
  textCheckNote,
  valuesText,
} from './logic';
import type { RdVolumeEditAccess } from './useRdVolumeEditAccess';
import { ErrorText, Muted, ReasonDialog } from './ui';

const Panel = styled.section`
  border: 1px solid ${({ theme }) => theme.colors.status.warning};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  padding: ${({ theme }) => theme.spacing(3)} ${({ theme }) => theme.spacing(4)};
  margin-bottom: ${({ theme }) => theme.spacing(4)};
`;

const Title = styled.h3`
  margin: 0 0 ${({ theme }) => theme.spacing(2)};
  font-size: 15px;
`;

const Item = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: ${({ theme }) => theme.spacing(3)};
  align-items: start;
  padding: ${({ theme }) => theme.spacing(2.5)} 0;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
  font-size: 13px;
  line-height: 1.5;

  @media (max-width: 760px) {
    grid-template-columns: minmax(0, 1fr);
  }
`;

const Strong = styled.span`
  font-weight: 600;
`;

const Actions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(1.5)};
  justify-content: flex-end;
`;

/** «Применить правку снова» / «Принять прочтение программы» — конфликт после
 *  переобработки и «строка не найдена» (тогда — к выбранной строке ревизии). */
export function ResolveEditButtons({
  edit,
  targets,
  onDone,
}: {
  edit: PtoVolumeEdit;
  /** Строки таблиц ревизии — для «строка не найдена». */
  targets?: readonly PtoVolumePosition[];
  onDone?: (message: string) => void;
}) {
  const [resolve, { isLoading }] = useResolvePtoVolumeEditMutation();
  const [target, setTarget] = useState('');
  const [error, setError] = useState<string | null>(null);
  const lost = edit.status === 'row_not_found';

  async function run(action: 'reapply' | 'close') {
    setError(null);
    try {
      await resolve({
        id: edit.id,
        body: { action, position_id: lost && action === 'reapply' ? target : undefined },
      }).unwrap();
      onDone?.(
        action === 'reapply'
          ? 'Правка применена снова.'
          : 'Принято прочтение программы — правка закрыта и осталась в истории.',
      );
    } catch (e) {
      setError(apiErrorText(e, 'Решение по правке не сохранено.'));
    }
  }

  return (
    <div>
      {lost && targets && (
        <Select
          aria-label="Строка ревизии, к которой применить правку"
          value={target}
          onChange={(e) => setTarget(e.target.value)}
          style={{ marginBottom: 6, height: 32, fontSize: 12.5 }}
        >
          <option value="">— строка ревизии для правки —</option>
          {targets.map((p) => (
            <option key={p.id} value={p.id}>
              {rowLabel(p) ?? `стр. ${p.page_number}`} — {p.name.slice(0, 70)}
            </option>
          ))}
        </Select>
      )}
      <Actions>
        <Button
          $variant="secondary"
          $size="sm"
          disabled={isLoading || (lost && !target)}
          onClick={() => void run('reapply')}
        >
          {lost ? 'Применить к строке' : 'Применить правку снова'}
        </Button>
        <Button $variant="ghost" $size="sm" disabled={isLoading} onClick={() => void run('close')}>
          Принять прочтение программы
        </Button>
      </Actions>
      {error && <ErrorText>{error}</ErrorText>}
    </div>
  );
}

/** Подтвердить / не подтверждать строку, не найденную в тексте листа. */
function PendingButtons({ edit, rights }: { edit: PtoVolumeEdit; rights: RdVolumeEditAccess }) {
  const [confirm, { isLoading: confirming }] = useConfirmPtoVolumeEditMutation();
  const [decline, { isLoading: declining }] = useDeclinePtoVolumeEditMutation();
  const [asking, setAsking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const check = canConfirmPending(edit, rights.me, rights);
  const own = !!rights.me && edit.created_by === rights.me;

  async function onConfirm() {
    setError(null);
    try {
      await confirm({ id: edit.id }).unwrap();
    } catch (e) {
      setError(apiErrorText(e, 'Строка не подтверждена.'));
    }
  }

  return (
    <div>
      <Actions>
        {!own && (
          <Button
            $variant="primary"
            $size="sm"
            disabled={!check.allowed || confirming}
            title={check.reason ?? 'Строка есть на листе РД — внести в объёмы'}
            onClick={() => void onConfirm()}
          >
            Подтвердить
          </Button>
        )}
        {rights.canEdit && (
          <Button $variant="ghost" $size="sm" onClick={() => setAsking(true)}>
            {own ? 'Отменить' : 'Не подтверждать'}
          </Button>
        )}
      </Actions>
      {own && (
        <Muted style={{ textAlign: 'right' }}>Подтверждает другой «Инженер+», не автор.</Muted>
      )}
      {error && <ErrorText>{error}</ErrorText>}
      {asking && (
        <ReasonDialog
          title={own ? 'Отменить внесённую строку' : 'Не подтверждать строку'}
          warning="Строка не попадёт в объёмы; запись останется в журнале."
          confirmLabel={own ? 'Отменить строку' : 'Не подтверждать'}
          busy={declining}
          error={error}
          onClose={() => setAsking(false)}
          onConfirm={(reason) => {
            setError(null);
            decline({ id: edit.id, reason })
              .unwrap()
              .then(() => setAsking(false))
              .catch((e: unknown) => setError(apiErrorText(e, 'Решение не сохранено.')));
          }}
        />
      )}
    </div>
  );
}

/**
 * «Правки ревизии, которые ждут решения» над таблицей позиций: строки, не
 * найденные в тексте листа (их подтверждает другой пользователь с правом
 * правки, не автор), конфликты и «строка не найдена» после переобработки.
 * Видно всем, кто видит экран; решения — «Инженер+».
 */
export function RevisionEditsPanel({
  projectId,
  revisionId,
  positions,
  rights,
}: {
  projectId: string;
  revisionId: string;
  positions: readonly PtoVolumePosition[];
  rights: RdVolumeEditAccess;
}) {
  const { data, error } = useListPtoVolumeEditsQuery(
    { projectId, revision_id: revisionId, status: ATTENTION_STATUSES.join(',') },
    { skip: !rights.enabled || !projectId || !revisionId },
  );
  const items = data?.items ?? [];
  const targets = useMemo(
    () => positions.filter((p) => isTableRow(p) && !isManualRow(p) && p.decision !== 'rejected'),
    [positions],
  );
  const byId = useMemo(() => new Map(positions.map((p) => [p.id, p])), [positions]);
  if (!rights.enabled) return null;
  if (error) {
    return (
      <Panel>
        <ErrorText>{apiErrorText(error, 'Не удалось загрузить правки ревизии.')}</ErrorText>
      </Panel>
    );
  }
  if (items.length === 0) return null;

  return (
    <Panel aria-label="Правки ревизии, которые ждут решения">
      <Title>Правки ревизии, которые ждут решения ({items.length})</Title>
      {items.map((e) => {
        const value = editValuesOf(e.value);
        const machine = editValuesOf(e.machine);
        const anchor = e.anchor_position_id ? byId.get(e.anchor_position_id) : undefined;
        const pos = e.position_id ? byId.get(e.position_id) : undefined;
        const sheet = sheetRefText(e.sheet_ref);
        return (
          <Item key={e.id}>
            <div>
              <div>
                <Strong>{kindLabel(e.kind)}</Strong> · {statusLabel(e.status)}
                {pos && rowLabel(pos) ? ` · ${rowLabel(pos)}` : ''}
              </div>
              <div>
                {e.status === 'pending' ? 'Строка' : 'Правка'}: {valuesText(value)}
                {machine && e.status !== 'pending'
                  ? ` (прочитано программой: ${valuesText(machine)})`
                  : ''}
              </div>
              {anchor && (
                <Muted>
                  После строки{' '}
                  {[rowLabel(anchor), `«${anchor.name.slice(0, 80)}»`].filter(Boolean).join(' ')}
                </Muted>
              )}
              {sheet && <Muted>Лист: {sheet}</Muted>}
              {e.status === 'pending' && <Muted>{textCheckNote(e.text_check)}</Muted>}
              {e.status === 'row_not_found' && (
                <Muted>
                  При переобработке строки с прочтением программы не нашлось — укажите строку
                  ревизии или примите прочтение программы.
                </Muted>
              )}
              <Muted>
                {e.created_by_name ?? 'пользователь'}, {dateTimeRu(e.created_at)}
                {e.comment ? ` · ${e.comment}` : ''}
              </Muted>
            </div>
            <div>
              {e.status === 'pending' ? (
                <PendingButtons edit={e} rights={rights} />
              ) : rights.canEdit ? (
                <ResolveEditButtons edit={e} targets={targets} />
              ) : (
                <Muted>Решает «Инженер+».</Muted>
              )}
            </div>
          </Item>
        );
      })}
    </Panel>
  );
}
